// src/components/rooms/BulkUploadModal.js

import React, { useState } from 'react';
import { X, Upload, Download, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { api } from '../../api';
import '../../styles/RoomsScreen.css';

const BulkUploadModal = ({ isOpen, onClose, onSuccess, propertyId }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            setFile(selected);
            setUploadError(null);
            setValidationErrors([]);
        }
    };

    const handleDownloadTemplate = () => {
        api.downloadRoomTemplate();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setUploadError('Please select a file to upload.');
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        setValidationErrors([]);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const result = await api.uploadBulkRooms(propertyId, formData);
            onSuccess(result.message);
            onClose(); 
            setFile(null); 
        } catch (error) {
            console.error("Upload failed", error);
            setUploadError(error.message);
            if (error.details && Array.isArray(error.details)) {
                setValidationErrors(error.details);
            }
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="room-modal-backdrop">
            <div className="room-modal-content large">
                <div className="room-modal-header">
                    <h3 className="room-modal-title">
                        <Upload size={20} />
                        Bulk Upload Rooms
                    </h3>
                    <button className="room-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="room-modal-body">
                    {/* Instructions */}
                    <div className="upload-instructions">
                        <p style={{ margin: '0 0 10px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AlertCircle size={16} /> Important Notes:
                        </p>
                        <ul className="upload-notes-list">
                            <li>Room Numbers must be unique.</li>
                            <li>If a Room Number already exists, it will be <strong>updated</strong>.</li>
                            <li>Columns required: <code>Room Number</code>, <code>Type</code>, <code>Floor</code>.</li>
                        </ul>
                    </div>

                    {/* Template Download */}
                    <div className="template-download-row">
                        <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Need the correct format?</span>
                        <button 
                            type="button" 
                            onClick={handleDownloadTemplate}
                            className="button button-secondary"
                            style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                        >
                            <Download size={14} style={{ marginRight: '6px' }} />
                            Download Template
                        </button>
                    </div>

                    {/* File Input */}
                    <form onSubmit={handleSubmit}>
                        <div className={`upload-area ${file ? 'has-file' : ''}`}>
                            <input 
                                type="file" 
                                id="file-upload" 
                                style={{ display: 'none' }} 
                                onChange={handleFileChange}
                                accept=".xlsx, .xls"
                            />
                            <label htmlFor="file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                                {file ? (
                                    <div style={{ color: '#15803d' }}>
                                        <FileSpreadsheet size={40} style={{ marginBottom: '10px' }} />
                                        <p style={{ margin: 0, fontWeight: '500' }}>{file.name}</p>
                                        <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem' }}>Click to change</p>
                                    </div>
                                ) : (
                                    <div style={{ color: '#64748b' }}>
                                        <Upload size={40} style={{ marginBottom: '10px', color: '#94a3b8' }} />
                                        <p style={{ margin: 0, fontWeight: '500' }}>Click to select Excel file</p>
                                        <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem' }}>.xlsx or .xls</p>
                                    </div>
                                )}
                            </label>
                        </div>

                        {/* Error Messages */}
                        {uploadError && (
                            <div className="room-error-message" style={{ marginTop: '15px' }}>
                                <strong>Error:</strong> {uploadError}
                                {validationErrors.length > 0 && (
                                    <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', maxHeight: '100px', overflowY: 'auto' }}>
                                        {validationErrors.map((err, i) => (
                                            <li key={i}>{err}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        <div className="room-modal-actions">
                            <button type="button" className="button button-secondary" onClick={onClose} disabled={isUploading}>
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="action-button-small" 
                                disabled={!file || isUploading}
                                style={{ opacity: (!file || isUploading) ? 0.7 : 1 }}
                            >
                                {isUploading ? 'Uploading...' : 'Start Import'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BulkUploadModal;